<template>
  <div class="login-card">
    <header class="card-header">
      <span class="portal-badge">{{ t('loginPage.portalBadge') }}</span>
      <h1 class="portal-title">{{ t('loginPage.portalTitle') }}</h1>
      <p class="portal-desc">{{ portalDesc }}</p>
    </header>

    <el-form
      ref="formRef"
      class="login-form"
      :rules="rules"
      label-position="top"
      :model="formLabelAlign"
      size="large"
      @submit.prevent
    >
      <el-form-item :label="t('field.username')" prop="username">
        <el-input
          v-model="formLabelAlign.username"
          :prefix-icon="User"
          :placeholder="t('loginPage.adminUsernamePh')"
          autocomplete="username"
        />
      </el-form-item>
      <el-form-item :label="t('loginPage.passwordLabel')" prop="password">
        <el-input
          v-model="formLabelAlign.password"
          :prefix-icon="Lock"
          type="password"
          :placeholder="t('loginPage.passwordPh')"
          autocomplete="current-password"
          show-password
          @keyup.enter="subimt(formRef)"
        />
      </el-form-item>

      <div class="form-row">
        <el-checkbox v-model="checked1">{{ t('loginPage.rememberUsername') }}</el-checkbox>
      </div>

      <el-button
        class="submit-btn"
        type="primary"
        :loading="state.loading"
        native-type="button"
        @click="subimt(formRef)"
      >
        {{ t('loginPage.loginBtn') }}
      </el-button>
    </el-form>

    <footer class="card-footer">
      <button type="button" class="link-btn" @click="goTenantLogin">
        {{ t('loginPage.backTenantLogin') }}
        <span class="link-arrow" aria-hidden="true">→</span>
      </button>
    </footer>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { User, Lock } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import { loginApi } from '@/api/login.js'
import { ElMessage } from 'element-plus'
import {
  DEFAULT_BRAND_NAME,
  applyPlatformAdminBranding,
} from '@/utils/tenantBranding.js'
import {
  clearLayoutMenuState,
  isLoginResponsePlatformAdmin,
  savePortalSession,
  PORTAL_PLATFORM,
} from '@/utils/authSession.js'
import { usePageI18n } from '@/composables/usePageI18n.js'

const brandName = DEFAULT_BRAND_NAME

const PLATFORM_TENANT_CODE = 'platform'
const router = useRouter()
const formRef = ref()
const checked1 = ref(true)
const state = reactive({ loading: false })

const rules = reactive({
  username: [{ required: true, message: '', trigger: 'blur' }],
  password: [{ required: true, message: '', trigger: 'blur' }],
})

const applyLabels = () => {
  rules.username[0].message = t('loginPage.usernameRequired')
  rules.password[0].message = t('loginPage.passwordRequired')
}
const { t } = usePageI18n(applyLabels)
applyLabels()

const portalDesc = computed(() => t('loginPage.portalDesc', { brand: brandName }))

const formLabelAlign = reactive({
  tenantCode: PLATFORM_TENANT_CODE,
  username: localStorage.getItem('platformAdminUsername') || '',
  password: '',
})

const goTenantLogin = () => {
  router.push('/login')
}

const subimt = (formEl) => {
  state.loading = true
  if (!formEl) {
    state.loading = false
    return
  }
  formEl.validate((valid) => {
    if (!valid) {
      state.loading = false
      return
    }
    const payload = {
      tenantCode: PLATFORM_TENANT_CODE,
      username: formLabelAlign.username,
      password: formLabelAlign.password,
    }
    loginApi(JSON.stringify(payload))
      .then((res) => {
        if (res.code == 200) {
          const isPlatformAdmin = isLoginResponsePlatformAdmin(res.data)
          if (!isPlatformAdmin) {
            ElMessage.error(t('loginPage.notPlatformAdmin'))
            return
          }
          applyPlatformAdminBranding()
          clearLayoutMenuState()
          if (checked1.value) {
            localStorage.setItem('platformAdminUsername', formLabelAlign.username)
          }
          savePortalSession(PORTAL_PLATFORM, {
            ...res.data,
            tenantCode: PLATFORM_TENANT_CODE,
          }, res.data.token)
          router.push('/platform/tenants')
        } else {
          ElMessage.error(res.msg)
        }
      })
      .finally(() => {
        setTimeout(() => {
          state.loading = false
        }, 300)
      })
  })
}
</script>

<style lang="less" scoped>
.login-card {
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 16px;
  padding: 40px 40px 32px;
  box-shadow:
    0 1px 2px rgba(24, 24, 27, 0.04),
    0 8px 30px rgba(24, 24, 27, 0.06);
}

.card-header {
  margin-bottom: 32px;
  padding-bottom: 28px;
  border-bottom: 1px solid #f4f4f5;
}

.portal-badge {
  display: inline-block;
  margin-bottom: 14px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6366f1;
  background: #eef2ff;
  border-radius: 6px;
}

.portal-title {
  margin: 0 0 10px;
  font-size: clamp(28px, 4vw, 32px);
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #18181b;
  line-height: 1.25;
}

.portal-desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #71717a;
}

.login-form {
  :deep(.el-form-item) {
    margin-bottom: 20px;
  }

  :deep(.el-form-item__label) {
    padding-bottom: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #3f3f46;
    line-height: 1.4;
  }

  :deep(.el-input__wrapper) {
    border-radius: 10px;
    box-shadow: 0 0 0 1px #e4e4e7 inset;
    padding: 4px 12px;
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 0 0 1px #d4d4d8 inset;
    }

    &.is-focus {
      box-shadow: 0 0 0 2px #6366f1 inset;
    }
  }

  :deep(.el-input__inner) {
    height: 42px;
    font-size: 15px;
    color: #18181b;
  }
}

.form-row {
  margin: -4px 0 22px;
}

:deep(.el-checkbox__label) {
  font-size: 13px;
  color: #71717a;
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background-color: #6366f1;
  border-color: #6366f1;
}

.submit-btn {
  width: 100%;
  height: 44px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  background: #6366f1;
  box-shadow: 0 1px 2px rgba(99, 102, 241, 0.2);
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: #4f46e5;
  }

  &:active {
    transform: translateY(1px);
  }
}

.card-footer {
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid #f4f4f5;
  text-align: center;
}

.link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: #6366f1;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: #4f46e5;
  }
}

.link-arrow {
  transition: transform 0.15s ease;
}

.link-btn:hover .link-arrow {
  transform: translateX(3px);
}

@media (max-width: 480px) {
  .login-card {
    padding: 28px 24px 24px;
  }
}
</style>
